import { faArrowDownWideShort } from "@fortawesome/pro-solid-svg-icons";
import { Column } from "@tanstack/react-table";

import { ColumnMultiValueFilter } from "src/base-components/ColumnFilter";
import { useFilter } from "src/base-components/tableColumnFilters/useFilters";
import { FeatureQueryStatus } from "src/clients/features-control/api";
import { Icon } from "src/design-system/Icon";

type GenericColumnType<T> = Column<T, unknown>;

export const StatusFilter = ({
  property,
}: {
  property?: string;
}): React.ReactElement => {
  const effectiveProperty = property ?? "status";
  const {
    filters: urlFilters,
    update,
    apply,
    reset,
    isFiltering,
    isApplied,
  } = useFilter(effectiveProperty, [
    { key: effectiveProperty, operator: "eq", values: [] },
  ]);
  const filter = urlFilters[0];

  return (
    <ColumnMultiValueFilter
      dataLoc={`${effectiveProperty}-filter`}
      defaultValue={undefined}
      headline="Status filter:"
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: effectiveProperty })}
      options={[
        { key: FeatureQueryStatus.ACTIVE, value: "active" },
        { key: FeatureQueryStatus.INACTIVE, value: "inactive" },
      ]}
      values={filter.values}
      onApply={apply}
      onChange={(values) => update([{ ...filter, values }])}
      onResetRequest={() => reset()}
    />
  );
};

export const EventTypeFilter = ({
  options,
}: {
  options?: string[];
}): React.ReactElement => {
  const {
    filters: urlFilters,
    update,
    apply,
    reset,
    isFiltering,
    isApplied,
  } = useFilter("event", [{ key: "event", operator: "eq", values: [] }]);
  const filter = urlFilters[0];

  const elements = (options ?? []).map((o) => ({ key: o, value: o }));

  return (
    <ColumnMultiValueFilter
      dataLoc="event-filter"
      defaultValue={undefined}
      headline="Event type filter:"
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: "event" })}
      options={elements}
      values={filter.values}
      onApply={apply}
      onChange={(values) => update([{ ...filter, values }])}
      onResetRequest={() => reset()}
    />
  );
};

export const EntityFilter = ({
  options,
  property,
}: {
  options?: string[];
  property?: string;
}): React.ReactElement => {
  const effectiveProperty = property ?? "entity_types";
  const {
    filters: urlFilters,
    update,
    apply,
    reset,
    isFiltering,
    isApplied,
  } = useFilter(effectiveProperty, [
    { key: effectiveProperty, operator: "eq", values: [] },
  ]);
  const filter = urlFilters[0];

  const elements = (options ?? []).map((o) => ({ key: o, value: o }));

  return (
    <ColumnMultiValueFilter
      dataLoc={`${effectiveProperty}-filter`}
      defaultValue={undefined}
      headline="Entity type filter:"
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: effectiveProperty })}
      options={elements}
      values={filter.values}
      onApply={apply}
      onChange={(values) => update([{ ...filter, values }])}
      onResetRequest={() => reset()}
    />
  );
};

export const SortActionButton = <T,>({
  column,
}: {
  column: GenericColumnType<T>;
}) => {
  const isSorted = column.getIsSorted();

  return (
    <button
      className="flex items-center"
      type="button"
      onClick={() => {
        let nextDesc: boolean | undefined;
        if (isSorted === "asc") {
          nextDesc = true;
        } else if (isSorted === "desc") {
          nextDesc = false;
        }
        column.clearSorting();
        column.toggleSorting(nextDesc);
      }}
    >
      <Icon
        variant={isSorted === "desc" ? "active" : "default"}
        icon={faArrowDownWideShort}
        size="xs"
      />
    </button>
  );
};
